import { cloneElement, useEffect, useRef, useState } from "react";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from "@/ui/context-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/popover";
import { Icone, ICONES, s } from "../visual.jsx";
import { useAcoes, useEstado, usePode } from "../estado.jsx";
import { confirmar } from "@/componentes/pergunta.tsx";
import { Cortina, MenuDeAcoes } from "./comuns.jsx";

/**
 * As ações de UMA mensagem, no gesto do WhatsApp Web.
 *
 * A hora e os tiques ficam FIXOS no canto da bolha — a referência de leitura não
 * pode dançar a cada hover. As ações moram em três lugares que não empurram nada:
 *
 *   - a seta no canto da bolha abre o menu com o resto;
 *   - o botão direito na bolha abre o MESMO menu, onde o ponteiro já está;
 *   - a fileira ao LADO da bolha, fora dela, tem os três gestos do dia:
 *     **responder**, **marcar prova** e **reagir**.
 *
 * Responder e prova subiram para lá porque eram as ações mais usadas e as mais
 * escondidas: responder custava dois cliques (abrir o menu, escolher) e marcar
 * prova custava três. São o gesto do aplicativo — visível ao passar o mouse, a um
 * toque.
 *
 * Cada item só existe se o provedor souber fazer aquilo (as capacidades chegam
 * com a mesa): reagir com emoji onde o provedor não sabe devolve erro que ninguém entende,
 * e editar mensagem fora do WhatsApp Web não existe.
 */

const EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/**
 * Um gesto visível ao lado da bolha.
 *
 * `resto` e `className` entram DEPOIS do que é nosso porque este botão também é
 * o gatilho do `Popover` da reação: quem abre é o `onClick` que a primitiva
 * injeta, e ele não pode ser apagado pelo `aoTocar` vazio deste caso.
 */
function Gesto({ rotulo, icone, cor = "var(--color-tinta-fantasma)", aoTocar, className, ...resto }) {
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={aoTocar}
      data-dica={rotulo}
      aria-label={rotulo}
      style={{ color: cor }}
      {...resto}
      /* `gesto`: em tela de toque o alvo cresce para 40px sem mexer no desenho
         nem na largura da goteira (`web/src/acesso.css`). `acaohover` é a
         opacidade que só acende com o ponteiro na bolha (`index.html`). */
      /* Mini da casa (28): era 26, a única medida da mesa fora das três. */
      className={`acaohover gesto size-mini flex-none rounded-full hover:bg-foreground/8 ${className ?? ""}`}
    >
      <Icone de={icone} tamanho={16} />
    </Button>
  );
}

/** O que esta mensagem permite, já filtrado pelas capacidades do provedor. */
function useOpcoes({ mensagem, nossa }) {
  const { wa, provas } = useEstado();
  const acoes = useAcoes();
  const pode = usePode();
  const capacidades = wa?.capacidades ?? {};
  /*
   * Capacidade do provedor NÃO é permissão de quem está olhando.
   *
   * Todos os gestos eram desenhados só pelo que o WhatsApp sabe fazer: quem tem
   * `leads:ver` e mais nada via "apagar para todos" e levava a recusa do
   * servidor DEPOIS do clique — no meio de um diálogo de confirmação que promete
   * apagar no celular do cliente. O resto da mesa já esconde pelo acesso
   * (arquivar, apagar o caso, "Sugerir"); aqui faltava.
   */
  const podeMandar = pode("leads:mensagem");
  const podeMarcarFato = pode("esteira:fatos");
  const podeAssistente = pode("automacao:agentes");

  /* Sem id externo o provedor não sabe de qual mensagem se trata: reagir, editar,
     apagar e citar dependem dele. Nota interna e recado do sistema ficam de fora
     sem alarde — eles não existem do lado do WhatsApp. */
  const noProvedor = Boolean(mensagem.externo_id);
  const marcada = (provas ?? []).some((p) => p.mensagem_id === mensagem.id);

  const itens = [];
  if (capacidades.texto && podeMandar)
    itens.push({
      chave: "encaminhar",
      rotulo: "Encaminhar",
      icone: ICONES.encaminhar,
      aoTocar: () => acoes.abrirEncaminhar(mensagem),
    });
  if (capacidades.editar_mensagem && podeMandar && nossa && noProvedor && mensagem.texto)
    itens.push({
      chave: "editar",
      rotulo: "Editar",
      detalhe: "O WhatsApp aceita até 15 minutos depois do envio",
      icone: ICONES.editar,
      aoTocar: () => acoes.abrirEdicao(mensagem),
    });
  if (mensagem.texto)
    itens.push({
      chave: "copiar",
      rotulo: "Copiar texto",
      icone: ICONES.documento,
      aoTocar: () => navigator.clipboard?.writeText(mensagem.texto ?? ""),
    });
  /* Selecionar abre o modo lote — encaminhar o print, o áudio e o número do
     processo num gesto em vez de três aberturas do mesmo modal. Vale também
     para quem só lê: copiar as selecionadas é leitura, e a barra é que esconde
     o que a pessoa não pode fazer. */
  /* Pedir ao ASSISTENTE sobre esta mensagem: é o caminho de "liga pra mim
     amanhã depois das 17" até um compromisso na agenda, sem trocar de tela e
     sem transcrever de memória. Só para quem opera assistente — a caixa termina
     na conversa dele, onde se autoriza a escrita. */
  if (podeAssistente)
    itens.push({
      chave: "assistente",
      rotulo: "Pedir ao assistente",
      detalhe: "Lembrete, tarefa, ficha — com este trecho na mão",
      icone: ICONES.assistente,
      aoTocar: () => acoes.pedirAoAssistente([mensagem.id]),
    });
  itens.push({
    chave: "selecionar",
    rotulo: "Selecionar",
    detalhe: "Para encaminhar ou apagar várias",
    icone: ICONES.certo,
    aoTocar: () => acoes.selecionar(mensagem.id),
  });
  if (capacidades.apagar_para_todos && podeMandar && noProvedor)
    itens.push({
      chave: "apagar",
      rotulo: "Apagar para todos",
      icone: ICONES.apagar,
      /* `perigo` é o que o menu do botão direito lê como `variant="destructive"`;
         `cor` é a mesma tinta para o menu da seta, que pinta pelo estilo. */
      perigo: true,
      cor: "var(--destructive)",
      /* Apagar para todos é irreversível e sai no celular do cliente: pergunta
         antes. Estava disparando no clique, dentro de um menu, ao lado de "copiar". */
      aoTocar: async () => {
        if (
          await confirmar({
            titulo: "Apagar esta mensagem para todos?",
            texto: "Ela some do WhatsApp do cliente também, e isso não se desfaz.",
            acao: "Apagar",
            perigo: true,
          })
        )
          acoes.apagarMensagem(mensagem.id);
      },
    });

  return {
    itens,
    marcada,
    podeMarcarFato,
    podeResponder: Boolean(capacidades.responder && podeMandar && noProvedor),
    responder: () => acoes.responderMensagem(mensagem),
    /* O menu do COMPUTADOR (botão direito e seta) também responde. Era só o
       gesto da goteira, que aparece no hover — a Laura (15/10) abriu o menu,
       como no WhatsApp, e concluiu que não dava para responder. */
    menu:
      capacidades.responder && podeMandar && noProvedor
        ? [{ chave: "responder", rotulo: "Responder", icone: ICONES.responder, aoTocar: () => acoes.responderMensagem(mensagem) }, ...itens]
        : itens,
    prova: () => (marcada ? acoes.abrirPeticao() : acoes.marcarProva(mensagem)),
    podeReagir: Boolean(capacidades.reagir && podeMandar && noProvedor),
    reagir: (e) => acoes.reagirMensagem(mensagem.id, e),
  };
}

/**
 * O botão direito na bolha abre o mesmo menu da seta.
 *
 * É o gesto que todo mundo tenta primeiro no computador, e ele não existia: o
 * ponteiro já está na mensagem, e a alternativa era caçar uma seta de 22px que
 * só aparece no hover. `ContextMenu` do shadcn faz a abertura, o teclado, o Esc
 * e o fechamento no clique fora — nada disso mora aqui.
 *
 * `render={children}` para a bolha ser o próprio gatilho: um `<div>` a mais em
 * volta dela quebraria o alinhamento do lado de quem fala. E `select-text`
 * porque a primitiva chega com `select-none` — copiar o texto da mensagem com o
 * mouse é a coisa mais feita nesta tela.
 */
export function MenuDaBolha({ mensagem, nossa, ativo, children }) {
  /* Sem gancho nenhum antes desta saída: a bolha desenha 200 vezes na abertura
     de uma conversa, e nota, eco e modo seleção não têm menu — não é hora de
     assinar o estado da mesa mais uma vez por bolha. */
  if (!ativo) return children;
  return (
    <ComMenuDaBolha mensagem={mensagem} nossa={nossa}>
      {children}
    </ComMenuDaBolha>
  );
}

/**
 * O apontador é DEDO? — pela mídia, não pela largura.
 *
 * `useCelular()` mede a janela, e a pergunta aqui é outra: quem toca nesta bolha
 * é um dedo ou um ponteiro? Tablet largo com dedo e notebook estreito com mouse
 * são os dois casos em que a largura responde errado. `pointer: coarse` é o que
 * o navegador tem para dizer isso.
 *
 * Lido UMA vez: o aparelho não troca de apontador no meio da conversa, e a
 * bolha desenha duzentas vezes na abertura de um caso.
 */
const APONTADOR_GROSSO = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;

function ComMenuDaBolha({ mensagem, nossa, children }) {
  const opcoes = useOpcoes({ mensagem, nossa });
  const { menu: itens } = opcoes;
  const toque = useGestosDeToque(opcoes);
  if (!itens.length) return children;

  /*
   * NO DEDO, só a folha — e é por isso que o `ContextMenu` não é montado.
   *
   * Segurar a bolha no celular abria DUAS coisas ao mesmo tempo: a folha de
   * baixo (nossa, pelo `setTimeout` de `useGestosDeToque`) e o menu de contexto
   * (do shadcn), porque o navegador dispara `contextmenu` no toque longo — o
   * mesmo gesto, dois ouvintes, duas superfícies empilhadas.
   *
   * `preventDefault` no `contextmenu` resolveria pela metade: a primitiva não
   * promete olhar `defaultPrevented`, e sobraria um menu montado esperando um
   * evento que nunca vem. Não montar é a resposta — o menu de contexto existe
   * para o BOTÃO DIREITO, que num aparelho de dedo não existe.
   */
  if (APONTADOR_GROSSO)
    return (
      <>
        {cloneElement(children, toque.aplicar(children))}
        {toque.folhaAberta && <FolhaDaMensagem opcoes={opcoes} aoFechar={toque.fecharFolha} />}
      </>
    );

  return (
    <ContextMenu>
      {/* Os gestos do dedo entram na PRÓPRIA bolha, por `cloneElement`: um
          invólucro a mais quebraria o alinhamento do lado de quem fala (é o
          mesmo motivo de o menu de contexto usar `render`). No ponteiro eles não
          existem — cada ouvinte sai fora na primeira linha, olhando o
          `pointerType`. */}
      <ContextMenuTrigger className="select-text" render={cloneElement(children, toque.aplicar(children))} />
      <ContextMenuContent className="max-w-[min(340px,92vw)] min-w-[214px] rounded-xl p-1.5">
        {itens.map((item) => (
          <ContextMenuItem
            key={item.chave}
            onClick={item.aoTocar}
            variant={item.perigo ? "destructive" : "default"}
            className={`gap-2.5 rounded-lg px-2.5 py-[9px] text-corpo font-semibold ${
              item.detalhe ? "items-start" : "items-center"
            }`}
          >
            {item.icone ? (
              <Icone
                de={item.icone}
                tamanho={16}
                /* Com detalhe embaixo o item fica alto: 1px é o que faz o ícone
                   nascer no centro da PRIMEIRA linha, não no topo da caixa. */
                estilo={s(`flex:none;${item.detalhe ? "margin-top:1px" : ""}`)}
              />
            ) : null}
            <span className="min-w-0">
              <span className="block">{item.rotulo}</span>
              {item.detalhe ? (
                <span className="block text-corpo leading-[1.45] font-medium text-tinta-media">{item.detalhe}</span>
              ) : null}
            </span>
          </ContextMenuItem>
        ))}
      </ContextMenuContent>
      {toque.folhaAberta && <FolhaDaMensagem opcoes={opcoes} aoFechar={toque.fecharFolha} />}
    </ContextMenu>
  );
}

/**
 * Segurar e arrastar — os dois gestos que o telefone tem e o ponteiro não.
 *
 * No celular a bolha não mostra botão nenhum (ver `index.html`), então estes dois
 * SÃO as ações da mensagem:
 *
 *   - **arrastar para o lado** responde, como no WhatsApp. É a ação mais usada
 *     da tela e agora custa um gesto, não uma caçada a um ícone de 26px;
 *   - **segurar** abre a folha com o resto, de baixo, ao alcance do polegar.
 *
 * A briga aqui é com a ROLAGEM: a conversa é uma lista que rola na vertical e o
 * gesto é horizontal, então o primeiro movimento decide de quem é o dedo — e
 * decidido é para sempre, até soltar. Sem isso, ou a conversa não rola (o gesto
 * come tudo) ou a bolha treme a cada rolagem. `touch-action:pan-y` diz ao
 * navegador a mesma coisa: a vertical é dele, a horizontal é nossa.
 */
const PUXAO_QUE_RESPONDE = 56;
const SEGURAR_MS = 420;

/* O tranco curto do aparelho, quando existe: é o que separa "a folha abriu" de
   "será que peguei?". No iOS não existe e o afundar da bolha faz o papel. */
const tremer = (ms) => navigator.vibrate?.(ms);
const linhaDaBolha = (alvo) => alvo.closest?.(".bolha") ?? null;

function useGestosDeToque({ podeResponder, responder }) {
  const [folhaAberta, setFolhaAberta] = useState(false);
  const gesto = useRef(null);
  const relogio = useRef(null);

  const desenhar = (g, dx) => {
    const razao = Math.min(1, dx / PUXAO_QUE_RESPONDE);
    g.bolha?.style.setProperty("--puxa", String(razao));
    if (g.bolha) g.bolha.dataset.puxa = dx >= PUXAO_QUE_RESPONDE ? "pronto" : "puxando";
    g.alvo.style.transform = `translateX(${dx}px)`;
    g.alvo.style.transition = "none";
  };

  const soltar = (g, { responde }) => {
    clearTimeout(relogio.current);
    g.alvo.dataset.segurando = "0";
    g.alvo.style.transform = "";
    /* A volta é animada, a ida não: quem está com o dedo na tela quer resposta
       imediata; quem soltou quer ver a bolha voltar. */
    g.alvo.style.transition = `transform var(--t-curta) var(--e-entra)`;
    g.bolha?.removeAttribute("data-puxa");
    g.bolha?.style.removeProperty("--puxa");
    if (responde) {
      tremer(12);
      responder();
    }
  };

  const props = {
    style: { touchAction: "pan-y" },
    onPointerDown: (e) => {
      if (e.pointerType !== "touch") return;
      const alvo = e.currentTarget;
      const g = { x: e.clientX, y: e.clientY, eixo: null, dx: 0, alvo, bolha: linhaDaBolha(alvo) };
      gesto.current = g;
      alvo.dataset.segurando = "1";
      relogio.current = setTimeout(() => {
        /* Só segura quem não saiu do lugar: rolar a conversa com o dedo parado
           por meio segundo não é "segurar a mensagem". */
        if (gesto.current !== g || g.eixo === "vertical") return;
        g.eixo = "segurou";
        alvo.dataset.segurando = "0";
        tremer(15);
        setFolhaAberta(true);
      }, SEGURAR_MS);
    },
    onPointerMove: (e) => {
      const g = gesto.current;
      if (!g || e.pointerType !== "touch" || g.eixo === "segurou") return;
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (!g.eixo) {
        /* 1.4 e não 1: a diagonal é da ROLAGEM. Empatado, quem ganha é a lista —
           errar para o lado trava a conversa, errar para cima só não responde. */
        if (Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx) / 1.4) {
          g.eixo = "vertical";
          clearTimeout(relogio.current);
          g.alvo.dataset.segurando = "0";
          return;
        }
        if (Math.abs(dx) > 10) {
          g.eixo = "lado";
          clearTimeout(relogio.current);
          g.alvo.dataset.segurando = "0";
        }
      }
      if (g.eixo !== "lado" || !podeResponder) return;
      /* Só para a direita, e com teto: o gesto é um só, e bolha que atravessa a
         tela vira arrastar-e-soltar, que não é o que ele é. */
      const antes = g.dx;
      g.dx = Math.max(0, Math.min(dx, PUXAO_QUE_RESPONDE + 22));
      if (antes < PUXAO_QUE_RESPONDE && g.dx >= PUXAO_QUE_RESPONDE) tremer(10);
      desenhar(g, g.dx);
    },
    onPointerUp: (e) => {
      const g = gesto.current;
      gesto.current = null;
      if (!g || e.pointerType !== "touch") return;
      soltar(g, { responde: podeResponder && g.eixo === "lado" && g.dx >= PUXAO_QUE_RESPONDE });
    },
    onPointerCancel: (e) => {
      const g = gesto.current;
      gesto.current = null;
      if (!g || e.pointerType !== "touch") return;
      soltar(g, { responde: false });
    },
  };

  useEffect(() => () => clearTimeout(relogio.current), []);

  /*
   * `style` do gesto se SOMA ao da bolha, nunca substitui.
   *
   * `cloneElement(filho, props)` troca a propriedade inteira: a bolha é pintada
   * por estilo inline (fundo, borda, raio, respiro — ver `PELES` em
   * `Trilha.jsx`), e mandar `{touchAction}` aqui apagou o fundo de toda bolha
   * que tem menu. A conversa ficou texto solto sobre a mesa, sem bolha nenhuma.
   */
  const aplicar = (filho) => ({ ...props, style: { ...filho.props?.style, ...props.style } });

  return { aplicar, folhaAberta, fecharFolha: () => setFolhaAberta(false) };
}

/**
 * A folha de ações da mensagem, no telefone.
 *
 * Sobe de baixo porque é lá que o polegar está — um menu ancorado na bolha
 * nasceria no topo da tela quando a mensagem estivesse no topo, que é metade das
 * vezes. A fileira de emoji vem PRIMEIRO e em cima, como no aplicativo: reagir é
 * a resposta mais curta que existe, e ela não pode custar uma rolagem de lista.
 */
function FolhaDaMensagem({ opcoes, aoFechar }) {
  const { itens, podeResponder, responder, marcada, podeMarcarFato, prova, podeReagir, reagir } = opcoes;
  const fazer = (o) => () => {
    o();
    aoFechar();
  };

  /* Responder e prova são gestos, não itens de menu — no computador eles moram
     na goteira ao lado da bolha. Aqui entram no topo da lista, que é o lugar
     equivalente: primeiro do alcance. */
  const tudo = [
    podeResponder && { chave: "responder", rotulo: "Responder", icone: ICONES.responder, aoTocar: responder },
    (marcada || podeMarcarFato) && {
      chave: "prova",
      rotulo: marcada ? "Ver na petição" : "Marcar como prova",
      icone: ICONES.starred,
      aoTocar: prova,
    },
    ...itens,
  ].filter(Boolean);

  return (
    <Cortina aoFechar={aoFechar} rotulo="Ações da mensagem" tamanho="estreita">
      {podeReagir && (
        <div className="mb-3 flex items-center justify-between gap-1 rounded-full bg-secondary p-1.5">
          {EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={fazer(() => reagir(e))}
              aria-label={`Reagir com ${e}`}
              className="flex size-[42px] flex-none items-center justify-center rounded-full text-[24px] leading-none transition-transform duration-[var(--t-toque)] active:scale-[.85]"
            >
              {e}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-col">
        {tudo.map((item) => (
          <button
            key={item.chave}
            type="button"
            onClick={fazer(item.aoTocar)}
            /* 52px de linha: é altura de lista de aplicativo, e a folha existe
               justamente porque o alvo de 26px da goteira não serve ao dedo. */
            className={`flex min-h-[52px] items-center gap-3.5 rounded-xl px-2 text-left text-forte font-medium transition-colors duration-[var(--t-toque)] active:bg-secondary ${
              item.perigo ? "text-destructive" : "text-foreground"
            }`}
          >
            {item.icone ? <Icone de={item.icone} tamanho={20} /> : null}
            <span className="min-w-0 flex-1">
              <span className="block">{item.rotulo}</span>
              {item.detalhe ? (
                <span className="block text-corpo leading-[1.4] font-normal text-tinta-media">{item.detalhe}</span>
              ) : null}
            </span>
          </button>
        ))}
      </div>
    </Cortina>
  );
}

/** A seta no canto da bolha: o resto do menu, sem empurrar nada de lugar. */
export function SetaDoMenu({ mensagem, nossa, escuro }) {
  const { menu: itens } = useOpcoes({ mensagem, nossa });
  if (!itens.length) return null;

  return (
    <span className="acaohover absolute top-0.5 right-1 z-[2]">
      <MenuDeAcoes
        rotulo="Ações da mensagem"
        titulo="Ações da mensagem"
        /* Classe e não estilo: o `style` não tinha hover, e a seta só dizia que
           era botão depois de aberta. 22 de desenho dentro do canto da bolha;
           o `alvo` do MenuDeAcoes dá os 40 de toque. */
        classe={`setamenu flex size-[22px] cursor-pointer items-center justify-center rounded-etiqueta border-0 bg-transparent transition-colors ${
          escuro
            ? "text-sobre-escuro/80 hover:bg-sobre-escuro/12 data-popup-open:bg-sobre-escuro/20"
            : "text-tinta-fantasma hover:bg-foreground/8 hover:text-foreground data-popup-open:bg-foreground/8"
        }`}
        itens={itens}
        ancora={nossa ? "bottom end" : "bottom start"}
        largura="214px"
      >
        <Icone de={ICONES.baixo} tamanho={14} />
      </MenuDeAcoes>
    </span>
  );
}

/** Os gestos do dia, fora da bolha, do lado de quem falou — como no aplicativo. */
export function GestosDaBolha({ mensagem, nossa }) {
  const { podeResponder, responder, marcada, podeMarcarFato, prova, podeReagir, reagir } = useOpcoes({ mensagem, nossa });
  const [aberta, setAberta] = useState(false);
  const relogio = useRef(null);

  return (
    <span className="flex flex-none items-center">
      {podeResponder && <Gesto rotulo="Responder" icone={ICONES.responder} aoTocar={responder} />}
      {/* Marcar prova é `esteira:fatos`; ver o que já foi marcado, não. */}
      {(marcada || podeMarcarFato) && (
        <Gesto
          rotulo={marcada ? "Ver na petição" : "Marcar como prova"}
          icone={ICONES.starred}
          cor={marcada ? "var(--color-verde)" : "var(--color-tinta-fantasma)"}
          aoTocar={prova}
        />
      )}
      {podeReagir && (
        /*
         * A fileira de emoji num `Popover`.
         *
         * Ela TROCAVA a fileira de gestos de lugar, porque a goteira ao lado da
         * bolha tem 30px e três ícones mais seis emojis empurrariam a bolha
         * para fora da coluna no celular. Num portal ancorado no botão isso
         * deixa de ser problema: a fileira flutua por cima da conversa e os
         * gestos ficam onde estavam.
         *
         * Fecha de três formas, porque foi o que faltou: clique fora e Esc são
         * da primitiva, e tirar o mouse continua sendo nosso — um seletor de
         * emoji que só fecha escolhendo emoji obriga a escolher algo para se
         * livrar dele.
         */
        <Popover open={aberta} onOpenChange={setAberta}>
          {/* `data-popup-open` porque o `acaohover` apaga o que não está sob o
              ponteiro: com a fileira aberta num portal, o foco sai da bolha e o
              próprio botão que abriu sumia. */}
          <PopoverTrigger render={<Gesto rotulo="Reagir" icone={ICONES.reagir} className="data-popup-open:opacity-100" />} />
          <PopoverContent
            side="top"
            align={nossa ? "end" : "start"}
            className="w-auto flex-row items-center gap-0.5 rounded-full border border-foreground/12 bg-card p-1 shadow-[0_8px_22px_color-mix(in_oklab,_var(--color-foreground)_16%,_transparent)]"
            /* Sair com o mouse fecha, mas com folga: passar por cima a caminho
               do emoji não pode fechar na cara de quem estava indo escolher. */
            onMouseLeave={() => {
              relogio.current = setTimeout(() => setAberta(false), 450);
            }}
            onMouseEnter={() => clearTimeout(relogio.current)}
          >
            {EMOJIS.map((e) => (
              <Button
                key={e}
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  reagir(e);
                  setAberta(false);
                }}
                aria-label={`Reagir com ${e}`}
                className="size-mini rounded-full text-forte leading-none"
              >
                {e}
              </Button>
            ))}
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}

/**
 * A reação já dada, colada embaixo da bolha.
 *
 * É onde o WhatsApp a mostra, e o lugar importa: reação é resposta curta ("ok",
 * "obrigado"), então ela pertence à mensagem e não à linha do tempo. Clicar tira
 * — emoji vazio é como o próprio WhatsApp desfaz.
 *
 * `Badge` do shadcn com `render={<button>}`: a pastilha é a mesma peça da
 * etiqueta de tese, e o que muda aqui é ela ser clicável.
 */
export function SeloDaReacao({ mensagem, nossa }) {
  const { podeReagir, reagir } = useOpcoes({ mensagem, nossa });
  if (!mensagem.reacao) return null;
  const rotulo = podeReagir ? "Tirar a reação" : "Reação";
  return (
    <Badge
      variant="outline"
      render={<button type="button" />}
      onClick={() => podeReagir && reagir("")}
      data-dica={podeReagir ? rotulo : undefined}
      aria-label={`${rotulo} ${mensagem.reacao}`}
      /* `relative` + `z-index` porque a margem negativa faz o selo SOBREPOR a
         bolha: sem contexto de empilhamento próprio ele ficava atrás — na bolha
         escura, metade do emoji desaparecia. */
      className={`alvo relative z-[2] mt-[-8px] mr-1 ml-1 h-auto rounded-full border-foreground/12 bg-card px-[7px] py-px text-corpo leading-[1.5] shadow-[0_2px_6px_color-mix(in_oklab,_var(--color-foreground)_8%,_transparent)] ${
        nossa ? "self-end" : "self-start"
      } ${podeReagir ? "cursor-pointer" : "cursor-default"}`}
    >
      {mensagem.reacao}
    </Badge>
  );
}
